import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';

export interface GuestsQuery {
  SortBy: string;
  SortDirection: string;
  Offset: number;
  Fetch: number;
  SearchText?: string;
}

export interface FeedbackQuery {
  SortBy: string;
  SortDirection: string;
  Offset: number;
  Fetch: number;
  /** Date — serialized as yyyy-M-d (day-granular, no leading zeros) to match AngularJS. */
  startDate: Date;
  /** Date — serialized as yyyy-M-d (day-granular, no leading zeros) to match AngularJS. */
  endDate: Date;
}

/** AngularJS urlFormatter serializes Dates here as `yyyy-M-d` (no leading zeros). */
function ymd(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/**
 * Native-Angular port of the AngularJS `Guests` service (account-v1 guests.js),
 * People-relevant endpoints. The query string MUST match the AngularJS request
 * byte-for-byte (param name + order) so the parity harness's recorded /api
 * fixtures replay identically — hence the explicit ordered fromObject.
 */
@Injectable()
export class GuestsApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment,
  ) {}

  /** GET /api/:prop/properties/:prop/guests?SortBy&SortDirection&Offset&Fetch&SearchText */
  guests(propertyUuid: string, query: GuestsQuery): Observable<any> {
    const params = new HttpParams({
      fromObject: {
        SortBy: query.SortBy,
        SortDirection: query.SortDirection,
        Offset: String(query.Offset),
        Fetch: String(query.Fetch),
        SearchText: query.SearchText ?? '',
      },
    });
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUuid}/properties/${propertyUuid}/guests`;
    return this.http.get(url, { params });
  }

  /** GET /api/:prop/properties/:prop/feedback?SortBy&SortDirection&Offset&Fetch&startDate&endDate */
  feedback(propertyUuid: string, query: FeedbackQuery): Observable<any> {
    const params = new HttpParams({
      fromObject: {
        SortBy: query.SortBy,
        SortDirection: query.SortDirection,
        Offset: String(query.Offset),
        Fetch: String(query.Fetch),
        startDate: ymd(query.startDate),
        endDate: ymd(query.endDate),
      },
    });
    const url = `${this.environment.apiV1BaseUrl}api/${propertyUuid}/properties/${propertyUuid}/feedback`;
    return this.http.get(url, { params });
  }
}
